import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface LoginFormProps {
  onSuccess?: () => void;
  onError?: (message: string) => void;
  isLoading?: boolean;
  onLoadingChange?: (loading: boolean) => void;
}

export function LoginForm({ 
  onSuccess, 
  onError, 
  isLoading = false,
  onLoadingChange 
}: LoginFormProps) {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!email || !password) {
      onError?.('이메일과 비밀번호를 입력해주세요');
      return;
    }

    onLoadingChange?.(true);
    
    // 타임아웃 설정 (30초)
    const timeoutId = setTimeout(() => {
      onLoadingChange?.(false);
      onError?.('로그인 시간이 초과되었습니다. 다시 시도해주세요.');
    }, 30000);

    try {
      const { success, error } = await signIn(email, password);
      
      clearTimeout(timeoutId);
      
      if (!success) {
        onError?.(error || '로그인에 실패했습니다');
        onLoadingChange?.(false);
      } else {
        // 로그인 성공 시 역할별 리다이렉트는 DesktopLoginPage에서 처리
        console.log('[LoginForm] 로그인 성공, 역할별 리다이렉트 대기 중...');
        onSuccess?.();
      }
    } catch (error) {
      clearTimeout(timeoutId);
      onError?.('로그인 중 오류가 발생했습니다');
      onLoadingChange?.(false);
    }
  };

  return (
    <form onSubmit={handleLogin} name="login-form" className="space-y-8">
      {/* 카카오 로그인 버튼 (추후 구현) */}
      <button
        type="button"
        disabled
        className="w-full h-[60px] relative opacity-50 cursor-not-allowed"
      >
        <img
          src="/icons/kakao_login.png"
          alt="카카오 로그인"
          className="w-full h-full object-contain"
        />
      </button>

      {/* 이메일 입력 */}
      <div>
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          이메일
        </label>
        <input
          type="email"
          name="email"
          id="email"
          autoComplete="username"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="이메일 입력"
          className="w-full h-[48px] px-5 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#86DB89] placeholder:text-[#979797] transition-colors"
        />
      </div>

      {/* 비밀번호 입력 */}
      <div>
        <label className="block text-[18px] font-medium text-[#222222] mb-3">
          비밀번호
        </label>
        <div className="relative">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            id="current-password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="비밀번호 입력"
            className="w-full h-[48px] px-5 pr-14 text-[18px] border-[1.5px] border-[#979797] rounded-[12px] focus:outline-none focus:border-[#86DB89] placeholder:text-[#979797] transition-colors"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-5 top-1/2 -translate-y-1/2"
          >
            <img
              src="/icons/icon_eye.svg"
              alt={showPassword ? '비밀번호 숨기기' : '비밀번호 보기'}
              className="w-6 h-6 opacity-60"
            />
          </button>
        </div>
      </div>

      {/* 로그인 버튼들 */}
      <div className="space-y-4">
        {/* 이메일 로그인 버튼 */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-[56px] bg-[#CCEFCC] text-[#222222] font-semibold text-[20px] rounded-xl hover:bg-[#B5E5B5] transition-colors disabled:opacity-50"
        >
          {isLoading ? '로그인 중...' : '이메일 로그인'}
        </button>
      </div>
    </form>
  );
}
